// Created on 2023-08-03
// Created by Vitaut Tryputsin
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { Subview } from "../view/subview.js";
/**
 * This enumeration defines the list of predefined layers, which can not be removed (but settings can be overridden).
 * It reflects values of corresponding C++ `Graphic3d_ZLayerId` enumeration defined by OCCT,
 * Custom layers might be added with positive index (>= 1) if standard list is insufficient for application needs;
 * these layers will be displayed on top of predefined ones.
 */
export var ZLayerId;
(function (ZLayerId) {
    /** identifier for invalid ZLayer */
    ZLayerId[ZLayerId["Unknown"] = -1] = "Unknown";
    /** default Z-layer for main presentations */
    ZLayerId[ZLayerId["Default"] = 0] = "Default";
    /** overlay for 3D presentations which inherits Depth from previous ZLayer */
    ZLayerId[ZLayerId["Top"] = -2] = "Top";
    /** overlay for 3D presentations with independent Depth */
    ZLayerId[ZLayerId["Topmost"] = -3] = "Topmost";
    /** overlay for 2D presentations (On-Screen-Display) */
    ZLayerId[ZLayerId["TopOSD"] = -4] = "TopOSD";
    /** underlay for 2D presentations (On-Screen-Display) */
    ZLayerId[ZLayerId["BotOSD"] = -5] = "BotOSD";
})(ZLayerId || (ZLayerId = {}));
/**
 * Interactive Object to be displayed in the Viewer.
 * This class stores a weak reference to the real object in memory identified by a string id.
 * User might specify id explicitly at construction time or rely on an id generated from loaded file URL.
 * Beware to use unique ids - creating multiple objects with the same id will result in logical issues!
 *
 * Usual lifetime of the object:
 * - Create an empty instance of subclass (object is not bound to Viewer);
 * - Construct or load object from the given URL (object is registered in the Viewer);
 * - Show/hide/modify object in the Viewer (e.g. [[InteractiveObject.visible]]);
 * - Remove object from the Viewer (object is unregistered from the Viewer and memory released) via [[InteractiveObject.remove]];
 *   alternatively, [[InteractiveContext.removeAllObjects]] interface could be used to remove all registered objects,
 *   in which case [[InteractiveObject]] instances will keep dead references ([[InteractiveObject.isExist]]).
 */
export class InteractiveObject {
    /**
    * Constructor.
    * @param {OccViewer} theModule viewer object
    * @param {ObjectId?} theId id of object (if it is not provided - will be generated automatically)
    */
    constructor(theModule, theId, thePrefix) {
        this._myViewer = theModule;
        this._myObjId = theId || theModule.interactiveContext.genObjectId(thePrefix || "");
    }
    /** Get object id as string. */
    get id() { return this._myObjId; }
    /** Return TRUE if object with specified Id exists. */
    get isExist() {
        return this._myViewer.interactiveContext.areObjectsInside(this._myObjId);
    }
    /** Return TRUE if object with specified Id has deferred data loaded. */
    get isLoaded() {
        return this._myViewer.interactiveContext.areObjectsLoaded(this._myObjId);
    }
    /** Return object visibility state. */
    get visible() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.displayed;
    }
    /** Set object visibility state. */
    set visible(theToShow) {
        if (theToShow)
            this._myViewer.interactiveContext.displayObjectList(this._myObjId, false);
        else
            this._myViewer.interactiveContext.eraseObjectList(this._myObjId, false);
    }
    /** Return view in which object is visible or NULL if it is not displayed or displayed in multiple views. */
    get visibleInView() {
        let aViewId = this._myViewer.interactiveContext.getVisibleInView(this._myObjId);
        if (aViewId == -2 || aViewId == -1) {
            return null;
        }
        let aView = this._myViewer.viewManager.findSubviewObject(aViewId);
        if (aView != null) {
            return aView;
        }
        let aViewWrap = new Subview();
        aViewWrap.wrap(this._myViewer, aViewId);
        return aViewWrap;
    }
    /**
     * Set object to be visible only within specific view or all views (NULL) - view affinity.
     * Note that [[isVisible]] still has to be set independently.
     */
    set visibleInView(theView) {
        this._myViewer.interactiveContext.setVisibleInView(this._myObjId, theView == null ? -2 : theView.id);
    }
    /** Return z layer of object. */
    get zLayer() {
        return this._myViewer.interactiveContext.getZLayer(this._myObjId);
    }
    /** Set z layer of object. */
    set zLayer(theLayer) {
        this._myViewer.interactiveContext.setZLayer(this._myObjId, theLayer);
    }
    /** Return object's global RGB color defined as [[ColorString]] */
    get color() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.color;
    }
    /** Set object's global color or empty string to unset. */
    set color(theColor) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.color = theColor != null ? theColor : "";
    }
    /** Return object's transparency within `[0.0, 1.0]` range, or `0.0` if unspecified. */
    get transparency() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.transparency;
    }
    /** Set object's global transparency within `[0.0, 1.0]` range, or `0.0` to unset. */
    set transparency(theTransp) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.transparency = theTransp != null ? theTransp : 0.0;
    }
    /** Remove object and invalidates it's id. */
    remove() {
        return this._myViewer.interactiveContext.removeObjectList(this._myObjId, false);
    }
    /** Get bounding box. */
    get bndBox() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.boundingBox;
    }
    /**
     * Clone
     * @return {ObjectId} cloned object id
     */
    _clone() {
        let newId = this._myViewer.interactiveContext.cloneObjectList(this._myObjId, false);
        this._myViewer.interactiveContext.eraseObjectList(newId, false);
        return newId;
    }
}
